import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { createMeeting, getTasks, getAgents } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Save, Loader2, CalendarClock, X, Check, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const NewMeetingPage = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [allTasks, setAllTasks] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [taskSearch, setTaskSearch] = useState('');
  const [agentSearch, setAgentSearch] = useState('');

  const [form, setForm] = useState({
    title: '',
    date: '',
    time: '',
    location: '',
    meeting_link: '',
    notes: '',
    task_ids: [],
    agent_ids: [],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tasksRes, agentsRes] = await Promise.all([
        getTasks({ sort_by: 'updated_at', sort_order: 'desc' }),
        getAgents()
      ]);
      setAllTasks(tasksRes.data);
      setAllAgents(agentsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const toggleTask = (taskId) => {
    setForm(prev => ({
      ...prev,
      task_ids: prev.task_ids.includes(taskId)
        ? prev.task_ids.filter(id => id !== taskId)
        : [...prev.task_ids, taskId]
    }));
  };

  const toggleAgent = (agentId) => {
    setForm(prev => ({
      ...prev,
      agent_ids: prev.agent_ids.includes(agentId)
        ? prev.agent_ids.filter(id => id !== agentId)
        : [...prev.agent_ids, agentId]
    }));
  };

  const handleTaskReorder = (result) => {
    if (!result.destination) return;
    const srcIdx = result.source.index;
    const destIdx = result.destination.index;
    if (srcIdx === destIdx) return;
    const reordered = Array.from(form.task_ids);
    const [moved] = reordered.splice(srcIdx, 1);
    reordered.splice(destIdx, 0, moved);
    setForm(prev => ({ ...prev, task_ids: reordered }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date) {
      toast.error('Titulo y fecha son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const response = await createMeeting(form);
      toast.success('Reunion creada');
      navigate(`/meetings/${response.data.id}`);
    } catch (error) {
      toast.error('Error al crear la reunion');
    } finally {
      setSaving(false);
    }
  };

  const filteredTasks = allTasks.filter(t =>
    t.title.toLowerCase().includes(taskSearch.toLowerCase()) ||
    t.sequential_number.includes(taskSearch)
  );

  const filteredAgents = allAgents.filter(a =>
    a.name.toLowerCase().includes(agentSearch.toLowerCase()) ||
    (a.company || '').toLowerCase().includes(agentSearch.toLowerCase())
  );

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-4xl mx-auto" data-testid="new-meeting-page">
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/meetings')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 font-['Manrope'] tracking-tight">
              Nueva Reunion
            </h1>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card className="bg-white border border-stone-200 shadow-sm">
            <CardHeader className="border-b border-stone-100 pb-4">
              <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                <CalendarClock className="w-5 h-5 text-slate-600" />
                Informacion
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Titulo *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Reunion de seguimiento..."
                  data-testid="meeting-title-input"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Fecha *</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    data-testid="meeting-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hora</Label>
                  <Input
                    type="time"
                    value={form.time}
                    onChange={(e) => setForm({ ...form, time: e.target.value })}
                    data-testid="meeting-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Lugar</Label>
                  <Input
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="Sala de juntas..."
                    data-testid="meeting-location-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Enlace reunion (Teams, Meet...)</Label>
                <Input
                  value={form.meeting_link}
                  onChange={(e) => setForm({ ...form, meeting_link: e.target.value })}
                  placeholder="https://teams.microsoft.com/..."
                  data-testid="meeting-link-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Notas previas a la reunion..."
                  rows={3}
                  data-testid="meeting-notes-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Tasks Selection */}
          <Card className="bg-white border border-stone-200 shadow-sm">
            <CardHeader className="border-b border-stone-100 pb-4">
              <CardTitle className="text-lg font-semibold text-slate-900">
                Temas (Tareas) - {form.task_ids.length} seleccionadas
                {form.task_ids.length > 1 && (
                  <span className="text-xs font-normal text-slate-400 ml-2">arrastra para reordenar</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {/* Selected tasks - draggable */}
              {form.task_ids.length > 0 && (
                <DragDropContext onDragEnd={handleTaskReorder}>
                  <Droppable droppableId="new-meeting-tasks" direction="vertical">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-1 mb-3 pb-3 border-b border-stone-100">
                        {form.task_ids.map((tid, idx) => {
                          const t = allTasks.find(t => t.id === tid);
                          if (!t) return null;
                          return (
                            <Draggable key={tid} draggableId={`new-${tid}`} index={idx}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`flex items-center gap-2 px-3 py-2 rounded border text-sm ${
                                    snapshot.isDragging
                                      ? 'bg-amber-50 border-amber-200 shadow-md'
                                      : 'bg-emerald-50 border-emerald-200'
                                  }`}
                                  data-testid={`selected-task-${idx}`}
                                >
                                  <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing" data-testid={`new-drag-handle-${idx}`}>
                                    <GripVertical className="w-4 h-4 text-slate-400" />
                                  </div>
                                  <span className="text-xs font-bold text-slate-500 w-5">{idx + 1}</span>
                                  <span className="font-mono text-xs text-emerald-600">{t.sequential_number}</span>
                                  <span className="text-emerald-800 truncate flex-1">{t.title}</span>
                                  <X className="w-4 h-4 cursor-pointer text-slate-400 hover:text-red-500 shrink-0" onClick={() => toggleTask(tid)} />
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
              <Input
                placeholder="Buscar tareas..."
                value={taskSearch}
                onChange={(e) => setTaskSearch(e.target.value)}
                className="mb-3"
                data-testid="task-search-input"
              />
              <div className="max-h-48 overflow-y-auto space-y-1">
                {filteredTasks.map(task => (
                  <label
                    key={task.id}
                    className="flex items-center gap-3 p-2 rounded hover:bg-stone-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={form.task_ids.includes(task.id)}
                      onChange={() => toggleTask(task.id)}
                      className="rounded border-stone-300"
                    />
                    <span className="font-mono text-xs text-slate-500">{task.sequential_number}</span>
                    <span className="text-sm text-slate-800 truncate">{task.title}</span>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Agents Selection */}
          <Card className="bg-white border border-stone-200 shadow-sm">
            <CardHeader className="border-b border-stone-100 pb-4">
              <CardTitle className="text-lg font-semibold text-slate-900">
                Agentes Convocados - {form.agent_ids.length} seleccionados
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <Input
                placeholder="Buscar agentes..."
                value={agentSearch}
                onChange={(e) => setAgentSearch(e.target.value)}
                className="mb-3"
                data-testid="agent-search-input"
              />
              {form.agent_ids.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-stone-100">
                  {form.agent_ids.map(aid => {
                    const a = allAgents.find(a => a.id === aid);
                    return a ? (
                      <span key={aid} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-sm px-2 py-1 rounded border border-blue-200">
                        {a.name}
                        <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={() => toggleAgent(aid)} />
                      </span>
                    ) : null;
                  })}
                </div>
              )}
              <div className="max-h-48 overflow-y-auto space-y-1">
                {filteredAgents.map(agent => (
                  <label
                    key={agent.id}
                    className="flex items-center gap-3 p-2 rounded hover:bg-stone-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={form.agent_ids.includes(agent.id)}
                      onChange={() => toggleAgent(agent.id)}
                      className="rounded border-stone-300"
                    />
                    <span className="text-sm text-slate-800">{agent.name}</span>
                    {agent.company && <span className="text-xs text-slate-400">({agent.company})</span>}
                    {agent.email && <span className="text-xs text-slate-400">{agent.email}</span>}
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" type="button" onClick={() => navigate('/meetings')}>
              Cancelar
            </Button>
            <Button type="submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="save-meeting-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Crear Reunion
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default NewMeetingPage;
